import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { hashPin } from "@/lib/pin";

const SESSION_KEY = "jozori_parent_unlocked_until";
const UNLOCK_MS = 15 * 60 * 1000; // 15 minutes

type GateState = {
  hasPin: boolean | null;
  unlocked: boolean;
  ensureUnlocked: () => Promise<boolean>;
  lock: () => void;
  refresh: () => Promise<void>;
};

const Ctx = createContext<GateState | null>(null);

export function ParentGateProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [hasPin, setHasPin] = useState<boolean | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);
  const [prompting, setPrompting] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setHasPin(null);
      return;
    }
    const { data } = await supabase.rpc("has_parent_pin");
    setHasPin(!!data);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Restore unlock from session
  useEffect(() => {
    if (typeof window === "undefined" || !user) return;
    const until = Number(sessionStorage.getItem(`${SESSION_KEY}:${user.id}`));
    if (until && until > Date.now()) setUnlocked(true);
  }, [user]);

  const lock = useCallback(() => {
    setUnlocked(false);
    if (user) sessionStorage.removeItem(`${SESSION_KEY}:${user.id}`);
  }, [user]);

  const ensureUnlocked = useCallback(async () => {
    if (unlocked) return true;
    if (hasPin === false) return true; // no PIN set yet
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setPrompting(true);
    });
  }, [unlocked, hasPin]);

  const handleAnswer = async (pin: string): Promise<string | null> => {
    if (!user) return "Not signed in";
    const h = await hashPin(pin, user.id);
    const { data, error } = await supabase.rpc("verify_parent_pin", { _hash: h });
    if (error) return "Verification failed";
    if (!data) return "Incorrect PIN";
    setUnlocked(true);
    sessionStorage.setItem(`${SESSION_KEY}:${user.id}`, String(Date.now() + UNLOCK_MS));
    setPrompting(false);
    resolverRef.current?.(true);
    resolverRef.current = null;
    return null;
  };

  const handleCancel = () => {
    setPrompting(false);
    resolverRef.current?.(false);
    resolverRef.current = null;
  };

  return (
    <Ctx.Provider value={{ hasPin, unlocked, ensureUnlocked, lock, refresh }}>
      {children}
      {prompting && <PinPromptModal onSubmit={handleAnswer} onCancel={handleCancel} />}
    </Ctx.Provider>
  );
}

export function useParentGate() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useParentGate must be used inside ParentGateProvider");
  return v;
}

function PinPromptModal({
  onSubmit,
  onCancel,
}: {
  onSubmit: (pin: string) => Promise<string | null>;
  onCancel: () => void;
}) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (p: string) => {
    setBusy(true);
    const err = await onSubmit(p);
    setBusy(false);
    if (err) {
      setError(err);
      setPin("");
    }
  };

  const press = (d: string) => {
    if (busy) return;
    setError(null);
    const next = (pin + d).slice(0, 4);
    setPin(next);
    if (next.length === 4) void submit(next);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background/85 backdrop-blur-md flex items-center justify-center p-5 animate-fade-in">
      <div className="bg-card border-2 border-clay/40 rounded-3xl shadow-2xl w-full max-w-sm p-6 animate-scale-in">
        <div className="text-center mb-5">
          <div className="text-5xl mb-2">🔒</div>
          <h2 className="font-display-en text-xl font-bold">Parent Zone</h2>
          <p className="font-display-ar text-base text-foreground/70 mt-1">منطقة الوالدين</p>
          <p className="text-sm text-foreground/60 mt-2">Enter your 4-digit PIN</p>
        </div>
        <div className="flex justify-center gap-3 mb-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`size-4 rounded-full border-2 transition-all ${
                i < pin.length ? "bg-primary border-primary scale-110" : "border-clay/60"
              }`}
            />
          ))}
        </div>
        {error && (
          <p className="text-center text-sm text-destructive font-bold mb-3 animate-fade-in">{error}</p>
        )}
        <div className="grid grid-cols-3 gap-2.5 mb-3">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"].map((k, idx) =>
            k === "" ? (
              <div key={idx} />
            ) : k === "⌫" ? (
              <button
                key={idx}
                onClick={() => {
                  setError(null);
                  setPin(pin.slice(0, -1));
                }}
                className="h-14 rounded-2xl bg-clay/25 hover:bg-clay/40 font-bold text-lg transition-colors"
              >
                ⌫
              </button>
            ) : (
              <button
                key={idx}
                onClick={() => press(k)}
                disabled={busy}
                className="h-14 rounded-2xl bg-clay/20 hover:bg-clay/40 active:scale-95 font-bold text-xl transition-all disabled:opacity-50"
              >
                {k}
              </button>
            ),
          )}
        </div>
        <button
          onClick={onCancel}
          className="w-full py-3 text-sm font-bold text-foreground/60 hover:text-foreground transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
